import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormControl, TextField } from "@mui/material";
import { useAppDispatch } from "@redux/hooks";
import { notifyUser } from "@redux/slices/CommonSlice";
import { PAGE_URLS } from "@constants/common";
import { ErrorResponseInterface } from "interfaces/common.interface";
import {
  CheckHasLowerCaseRegex,
  checkHasOneNumber,
  checkHasOneSpecialCharacter,
  CheckHasUpperCaseRegex,
} from "@utils/regex";
import { useResetPasswordMutation } from "../authApi";
import {
  LoginInfoIcon,
  PasswordHide,
  PasswordView,
  YggLogo,
} from "@features/common/SvgIcons";
import PasswordValidation from "@features/common/passwordValidation/PasswordValidation";
import Button from "@features/common/button/Button";
import HelpText from "../helpText/HelpText";
import FbWarning from "../fbWarning/FbWarning";
import styles from "./ResetPassword.module.scss";

interface ResetPasswordProps {
  email: string;
}

const ResetPassword: FC<ResetPasswordProps> = ({ email }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [showValidation, setShowValidation] = useState(false);
  const [confirmPasswordHelperText, setConfirmPasswordHelperText] =
    useState("");
  const [showFbWarning, setShowFbWarning] = useState(false);

  const [
    resetPassword,
    {
      isSuccess: resetPasswordIsSuccess,
      error: resetPasswordError,
      data: resetPasswordData,
      isLoading: resetPasswordLoading,
    },
  ] = useResetPasswordMutation();

  const handleFBLogin = () => {
    setShowFbWarning(true);
  };

  const validateResetPasswordForm = () => {
    let isValid = false;

    const conditions = [
      CheckHasUpperCaseRegex.test(newPassword),
      CheckHasLowerCaseRegex.test(newPassword),
      checkHasOneNumber(newPassword),
      checkHasOneSpecialCharacter(newPassword),
      newPassword.length >= 8,
    ];

    const conditionsCount = conditions.filter((condition) => condition).length;

    if (conditionsCount !== conditions.length) {
      setConfirmPasswordHelperText(
        "Must contain at least 8 digits consisting of 1 uppercase character, 1 lowercase character, 1 number and 1 special character. (.,/!@#$%^&*) and a maximum of 16 digits."
      );
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordHelperText("Password do not match");
    } else {
      isValid = true;
    }

    return isValid;
  };

  const handleRedirectToLogin = () => {
    window.setTimeout(() => {
      navigate(PAGE_URLS.LOGIN, {
        state: { passwordResetSuccess: true },
        replace: true,
      });
    }, 3000);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValidForm = validateResetPasswordForm();

    if (isValidForm) {
      resetPassword({
        new_password1: newPassword,
        new_password2: confirmPassword,
        username: email,
      });
    }
  };

  // Enable submit when both fields are filled and password is valid
  useEffect(() => {
    if (confirmPassword && newPassword && isPasswordValid) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [confirmPassword, newPassword, isPasswordValid]);

  // Handle API response
  useEffect(() => {
    if (resetPasswordIsSuccess) {
      dispatch(
        notifyUser({
          message: (resetPasswordData as any)?.message,
          severity: "success",
        })
      );
      handleRedirectToLogin();
    }
    if (resetPasswordError) {
      setConfirmPasswordHelperText(
        (resetPasswordError as ErrorResponseInterface).message
      );

      dispatch(
        notifyUser({
          message: (resetPasswordError as any)?.data?.message,
          severity: "error",
        })
      );
    }
  }, [resetPasswordIsSuccess, resetPasswordError, resetPasswordData]);

  return (
    <div className={styles["reset-password"]}>
      <div className={styles["header"]}>
        <YggLogo />
        <h5>Enter Password</h5>
      </div>

      <form
        className={`${styles["reset-password__form"]} reset-password-form`}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <div className={`${styles["password-input-button"]} email-login`}>
          <div className={`${styles["password-input-button__input"]}`}>
            <FormControl variant="standard" fullWidth={true}>
              <TextField
                autoFocus={true}
                name="password"
                value={newPassword}
                id="new-password"
                type={showNewPassword ? "text" : "password"}
                variant="standard"
                placeholder="New Password"
                slotProps={{
                  input: {
                    disableUnderline: true,
                  },
                }}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                }}
                onFocus={() => setShowValidation(true)}
              />
            </FormControl>

            {newPassword?.length > 0 && (
              <button
                type="button"
                onClick={handleToggleNewPasswordVisibility}
                className={styles["password-visibility-toggle"]}
              >
                {!showNewPassword ? <PasswordHide /> : <PasswordView />}
              </button>
            )}
          </div>
        </div>

        <div style={{ width: "100%" }}>
          <div
            className={`${styles["password-input-button"]} email-login ${
              confirmPasswordHelperText ? styles["error"] : ""
            }`}
          >
            <div className={`${styles["password-input-button__input"]}`}>
              <FormControl variant="standard" fullWidth={true}>
                <TextField
                  autoFocus={false}
                  name="confirmPassword"
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  variant="standard"
                  slotProps={{
                    input: {
                      disableUnderline: true,
                    },
                  }}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                  }}
                  onFocus={() => {
                    setConfirmPasswordHelperText("");
                    setShowValidation(false);
                  }}
                />
              </FormControl>

              {confirmPassword?.length > 0 && (
                <button
                  type="button"
                  onClick={handleToggleConfirmPasswordVisibility}
                  className={styles["password-visibility-toggle"]}
                >
                  {!showConfirmPassword ? <PasswordHide /> : <PasswordView />}
                </button>
              )}
            </div>
          </div>

          {confirmPasswordHelperText && (
            <p className={styles["error-text"]}>{confirmPasswordHelperText}</p>
          )}
        </div>

        <PasswordValidation
          password={newPassword}
          setIsPasswordValid={setIsPasswordValid}
          isVisible={showValidation}
        />

        <div style={{ marginTop: "24px", width: "100%" }}>
          <Button
            id="btnSubmit"
            theme="purple"
            attribues={{
              disabled: isSubmitDisabled || resetPasswordLoading,
              type: "submit",
            }}
            className={`submit-button ${styles["email-input-button"]}`}
          >
            Set Password
          </Button>
        </div>
      </form>

      <div className={styles["trouble-signin-container"]}>
        <div className={styles["trouble-signin"]}>
          <LoginInfoIcon />
          <span className={styles["content"]} onClick={handleFBLogin}>
            Trouble signing in?
          </span>
        </div>
        <HelpText />
      </div>

      <FbWarning open={showFbWarning} onClose={setShowFbWarning} />
    </div>
  );
};

export default ResetPassword;
